// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * PRÉFÉRENCES DE TABLEAU, CÔTÉ ÉCRAN — module PUR (L1, 10/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La préférence d'un tableau (ordre, masquées, épinglées, largeurs, tri) vit
 * EN BASE, une ligne par (personne, tableau), et suit donc la personne d'une
 * paillasse à l'autre. Le `localStorage` n'est plus que son CACHE :
 *
 *  · à l'ouverture, on part du cache (synchrone, pas de saut d'écran), puis on
 *    demande la ligne au serveur et on la fait prévaloir ;
 *  · chaque réglage s'écrit dans le cache ET au serveur ;
 *  · une PANNE RÉSEAU ne retire rien : l'écran garde ce que le cache sait, et
 *    le réglage suivant retentera l'écriture. Aucun message — un confort qui
 *    s'excuse à chaque relecture serait pire que le silence.
 *
 * ── Migration, une fois ─────────────────────────────────────────────────────
 * Avant L1, `TableauTriable` écrivait `bio24.colonnes.<id>` (l'ordre) et
 * `bio24.largeurs.<id>` ; L0 a ajouté `bio24.tableau.<id>` ; `RhParcours`
 * garde `bio24.parcours.colonnes` (tableau `rh-parcours`, migré au socle en
 * L5). Au PREMIER chargement, tout cela est envoyé en base — y compris pour
 * les tableaux qu'on n'a pas encore rouverts — puis marqué migré. Les clés
 * historiques ne sont PAS effacées : `RhParcours` lit encore la sienne
 * jusqu'à L5, et une clé qui traîne ne gêne personne.
 *
 * Aucun accès direct au navigateur : le cache et le serveur sont INJECTÉS
 * (`CacheLocal`, `DepotPrefs`), c'est ce qui rend ce module rejouable par
 * `backend/tests/front-tableau-prefs.test.js`. L'implémentation réelle est
 * dans `tableauDepotPrefs.ts`.
 */
import {
  lirePref, ecartsPref, colonnesOrdonnees, PREF_VIDE, cleStockage, cleOrdreHistorique, cleLargeursHistorique,
  type PrefTableau,
} from './tableauMoteur';

/** Ce que le serveur rend pour un tableau (`GET /ged/pref-tableau/:id`). */
export interface ReponsePref {
  /** La ligne de l'appelant ; vide ou absente = pas de ligne (le serveur n'en garde jamais une au défaut). */
  pref: unknown;
  obligatoires: string[];
  /** La RQ peut imposer des colonnes depuis le tableau. */
  peutRegler: boolean;
}

export interface DepotPrefs {
  /** Rend TOUJOURS les obligatoires et le droit de régler ; rejette en cas de panne. */
  lire(id: string, colonnes: readonly string[]): Promise<ReponsePref>;
  ecrire(id: string, colonnes: readonly string[] | null, ecarts: Partial<PrefTableau>): Promise<void>;
  effacer(id: string): Promise<void>;
  imposer(id: string, colonnes: readonly string[], connues: readonly string[]): Promise<string[]>;
}

export interface CacheLocal {
  lire(cle: string): string | null;
  ecrire(cle: string, valeur: string): void;
  effacer(cle: string): void;
  cles(): string[];
}

export const CLE_MIGRATION = 'bio24.prefs.migrees';
/** `RhParcours` tient sa propre clé jusqu'à L5 ; en base, c'est le tableau `rh-parcours`. */
export const CLE_PARCOURS = 'bio24.parcours.colonnes';
export const ID_PARCOURS = 'rh-parcours';

/* ───────────────────────────── Cache ─────────────────────────────────────── */

const json = (s: string | null): unknown => { try { return s ? JSON.parse(s) : null; } catch { return null; } };

/** La préférence telle que le cache la connaît : clé neuve d'abord, clés historiques en repli. */
export function prefDuCache(cache: CacheLocal, id: string, cles: readonly string[]): PrefTableau {
  const neuve = lirePref(cache.lire(cleStockage(id)));
  const ordreH = lirePref({ ordre: json(cache.lire(cleOrdreHistorique(id))) }).ordre;
  const largeursH = lirePref({ largeurs: json(cache.lire(cleLargeursHistorique(id))) }).largeurs;
  const p: PrefTableau = {
    ...neuve,
    ordre: neuve.ordre.length ? neuve.ordre : ordreH,
    largeurs: Object.keys(neuve.largeurs).length ? neuve.largeurs : largeursH,
  };
  return { ...p, ordre: cles.length ? colonnesOrdonnees(cles, p) : p.ordre };
}

/** N'écrit que les écarts ; efface la clé quand il n'y en a plus. Ne lève jamais. */
export function ecrireCache(cache: CacheLocal, id: string, cles: readonly string[], pref: PrefTableau): void {
  try {
    const e = ecartsPref(cles, pref);
    if (e) cache.ecrire(cleStockage(id), JSON.stringify(e)); else cache.effacer(cleStockage(id));
  } catch { /* navigation privée */ }
}

/* ───────────────────────────── Chargement ────────────────────────────────── */

export type SourcePref = 'serveur' | 'cache' | 'migre';

export interface PrefChargee {
  pref: PrefTableau;
  source: SourcePref;
  obligatoires: string[];
  peutRegler: boolean;
  /** Une ligne existe en base pour (personne, tableau) — même « au défaut » : la personne a déjà réglé ce tableau. */
  ligne?: boolean;
}

/**
 * La préférence à APPLIQUER, et d'où elle vient :
 *  · `serveur` — la ligne en base (elle prévaut, et rafraîchit le cache) ;
 *  · `migre`   — pas de ligne, mais un cache non vide : il est ENVOYÉ, puis appliqué ;
 *  · `cache`   — le serveur n'a pas répondu : on garde ce que le poste sait.
 */
export async function chargerPref(o: {
  id: string; cles: readonly string[]; cache: CacheLocal; serveur: DepotPrefs;
}): Promise<PrefChargee> {
  const locale = prefDuCache(o.cache, o.id, o.cles);
  let reponse: ReponsePref;
  try {
    reponse = await o.serveur.lire(o.id, o.cles);
  } catch {
    return { pref: locale, source: 'cache', obligatoires: [], peutRegler: false };
  }
  const enBase = lirePref(reponse.pref);
  /* Une ligne qui ne porte que l'ordre d'origine existe quand même : elle garde l'affichage d'une colonne masquée par défaut. */
  const ligne = enBase.ordre.length > 0 || !!ecartsPref(o.cles, enBase);
  const extra = { obligatoires: reponse.obligatoires ?? [], peutRegler: !!reponse.peutRegler, ligne };
  const appliquee: PrefTableau = { ...enBase, ordre: colonnesOrdonnees(o.cles, enBase) };
  if (ecartsPref(o.cles, appliquee)) {
    ecrireCache(o.cache, o.id, o.cles, appliquee);
    return { pref: appliquee, source: 'serveur', ...extra };
  }
  /* Pas de ligne en base. Si le poste avait un réglage, c'est lui qui devient
     la ligne — sinon changer de paillasse perdrait ce qu'on venait de régler. */
  const e = ecartsPref(o.cles, locale);
  if (e) {
    try { await o.serveur.ecrire(o.id, o.cles, e); } catch { /* la prochaine écriture retentera */ }
    return { pref: locale, source: 'migre', ...extra };
  }
  return { pref: { ...PREF_VIDE, ordre: [...o.cles] }, source: 'serveur', ...extra };
}

/* ───────────────────────────── Migration ─────────────────────────────────── */

/** Les identifiants de tableau que le cache connaît, d'après ses clés. */
export function idsDuCache(cles: readonly string[]): string[] {
  const ids = new Set<string>();
  for (const k of cles) {
    const m = /^bio24\.(colonnes|largeurs|tableau)\.(.+)$/.exec(k);
    if (m && m[2]) ids.add(m[2]);
    if (k === CLE_PARCOURS) ids.add(ID_PARCOURS);
  }
  return [...ids].sort();
}

/** La préférence d'un tableau telle que le cache la porte, SANS liste de colonnes (le serveur relira). */
function prefLocaleBrute(cache: CacheLocal, id: string): Partial<PrefTableau> | null {
  if (id === ID_PARCOURS) {
    const p = lirePref(cache.lire(CLE_PARCOURS));
    return p.ordre.length || p.masquees.length ? { ordre: p.ordre, masquees: p.masquees } : null;
  }
  const p = prefDuCache(cache, id, []);
  const e: Partial<PrefTableau> = {};
  if (p.ordre.length) e.ordre = p.ordre;
  if (p.masquees.length) e.masquees = p.masquees;
  if (p.epinglees.length) e.epinglees = p.epinglees;
  if (Object.keys(p.largeurs).length) e.largeurs = p.largeurs;
  if (p.tri) e.tri = p.tri;
  return Object.keys(e).length ? e : null;
}

/**
 * Envoie en base tout ce que le cache sait, UNE fois. Le marqueur n'est posé
 * que si TOUT est passé : une panne à mi-chemin rejouera la migration au
 * prochain chargement, et réécrire une préférence identique est sans effet.
 */
export async function migrerPrefsLocales(o: { cache: CacheLocal; serveur: DepotPrefs }): Promise<{ migrees: string[]; echecs: string[] }> {
  const migrees: string[] = []; const echecs: string[] = [];
  if (o.cache.lire(CLE_MIGRATION)) return { migrees, echecs };
  for (const id of idsDuCache(o.cache.cles())) {
    const e = prefLocaleBrute(o.cache, id);
    if (!e) continue;
    try {
      /* On n'écrase pas une ligne que le serveur a déjà (réglée depuis une
         autre paillasse) : le serveur fait foi, le cache n'est qu'un repli. */
      const enBase = lirePref((await o.serveur.lire(id, [])).pref);
      if (enBase.ordre.length || enBase.masquees.length || enBase.epinglees.length || Object.keys(enBase.largeurs).length || enBase.tri) continue;
      await o.serveur.ecrire(id, null, e);
      migrees.push(id);
    } catch { echecs.push(id); }
  }
  if (!echecs.length) { try { o.cache.ecrire(CLE_MIGRATION, '1'); } catch { /* privé */ } }
  return { migrees, echecs };
}
